import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Quote, Download, Award, Leaf, ShieldCheck, BadgeCheck } from "lucide-react";
import Hero from "../components/Hero";
import { serviceIcons } from "../components/icons";
import { products, services, testimonials, stats, aboutImage, company, certifications, facilityGallery } from "../mock/mock";

const certIcons = { Award, Leaf, ShieldCheck, BadgeCheck };

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-white">
      <Hero />

      {/* Certifications trust bar */}
      <section className="border-b border-gray-100 bg-[#fafafa]">
        <div className="max-w-[1140px] mx-auto px-5 py-6">
          <div className="flex flex-col md:flex-row items-center gap-5 md:gap-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#1a1a1a]/50 shrink-0">
              Certified &amp; Compliant
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              {certifications.map((c) => {
                const Icon = certIcons[c.icon];
                return (
                  <div key={c.code} className="flex items-center gap-2.5">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#D80000]/10">
                      {Icon && <Icon className="w-4 h-4 text-[#D80000]" />}
                    </span>
                    <div className="leading-tight">
                      <p className="font-heading font-semibold text-sm text-[#1a1a1a]">{c.code}</p>
                      <p className="text-[11px] text-[#1a1a1a]/60">{c.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-[900px] mx-auto px-5 py-16 md:py-20">
        <p className="text-[17px] md:text-[18px] leading-8 text-[#1a1a1a]/90 mb-7">
          {company.name} is an India-based engineering and manufacturing company
          focused on delivering reliable, precision-engineered components and
          manufacturing solutions to OEMs, engineering companies, equipment
          manufacturers, industrial buyers, contractors and distributors.
        </p>
        <p className="text-[17px] md:text-[18px] leading-8 text-[#1a1a1a]/90">
          Operating from our 10,000+ sq. ft. manufacturing facility in Vasai
          East, Maharashtra, we bring together experienced professionals,
          skilled technicians, engineers and production personnel. With a
          workforce of 100+ employees and a strong foundation in engineering,
          production, quality control and customer support, we undertake
          customized manufacturing requirements &mdash; from individual
          components and prototypes to regular production.
        </p>
      </section>

      {/* Products We Manufacture (red) */}
      <section id="products" className="bg-[#D80000] py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-5">
          <h2 className="font-heading text-white text-3xl md:text-4xl text-center title-accent">
            Products We Manufacture
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-[860px] mx-auto">
            {products.map((p) => (
              <button
                key={p.slug}
                onClick={() => navigate(`/products/${p.slug}`)}
                className="group flex items-center justify-between bg-white rounded-md px-6 py-5 text-left shadow-sm hover:shadow-xl hover:-translate-y-0.5"
              >
                <span className="font-heading font-semibold text-[#1a1a1a] text-[15px] md:text-base group-hover:text-[#D80000]">
                  {p.title}
                </span>
                <ArrowRight className="w-5 h-5 text-[#D80000] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section id="services" className="py-16 md:py-24">
        <div className="max-w-[1180px] mx-auto px-5">
          <h2 className="font-heading text-[#1a1a1a] text-3xl md:text-4xl text-center title-accent">
            Our Services
          </h2>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((s) => {
              const Icon = serviceIcons[s.icon];
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group text-center flex flex-col items-center"
                >
                  <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#f3f4f6] group-hover:bg-[#D80000] transition-colors">
                    {Icon && <Icon className="w-9 h-9 text-[#D80000] group-hover:text-white transition-colors" strokeWidth={1.8} />}
                  </span>
                  <h3 className="font-heading font-semibold text-lg text-[#1a1a1a] mt-5 group-hover:text-[#D80000]">
                    {s.title}
                  </h3>
                  <p className="text-sm text-[#1a1a1a]/70 leading-relaxed mt-2 px-2">
                    {s.tagline}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-[#f7f7f8] py-16 md:py-20">
        <div className="max-w-[760px] mx-auto px-5 text-center">
          <Quote className="w-10 h-10 text-[#D80000] mx-auto mb-6" />
          <p className="font-heading text-xl md:text-2xl leading-relaxed text-[#1a1a1a]">
            &ldquo;{testimonials[0].quote}&rdquo;
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <img src={testimonials[0].avatar} alt={testimonials[0].name} className="w-12 h-12 rounded-full" />
            <div className="text-left">
              <p className="font-semibold text-[#1a1a1a]">{testimonials[0].name}</p>
              <p className="text-sm text-[#1a1a1a]/60">{testimonials[0].role}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 border-y border-gray-100">
        <div className="max-w-[1100px] mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((st) => (
            <div key={st.label}>
              <p className="font-heading text-4xl md:text-5xl font-bold text-[#D80000]">{st.value}</p>
              <p className="text-sm text-[#1a1a1a]/70 mt-2">{st.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Facility teaser */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-5">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <span className="text-[#D80000] font-heading font-semibold tracking-widest text-sm">OUR FACILITY</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1a1a1a] mt-2">Everything Made Under One Roof</h2>
            </div>
            <Link to="/facility" className="group inline-flex items-center gap-2 text-[#D80000] font-semibold">
              View Full Gallery <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {facilityGallery.slice(0, 4).map((img) => (
              <Link to="/facility" key={img.src} className="group relative overflow-hidden rounded-lg h-48">
                <img src={img.src} alt={img.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <p className="absolute bottom-3 left-3 right-3 text-white font-heading font-semibold text-sm">{img.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About teaser */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1140px] mx-auto px-5 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[#D80000] font-heading font-semibold tracking-widest text-sm">ABOUT US</span>
            <h2 className="font-heading text-3xl md:text-[38px] leading-tight font-semibold text-[#1a1a1a] mt-3">
              Why Choose {company.name}? Everything Made In-House.
            </h2>
            <p className="text-[#1a1a1a]/80 leading-8 mt-5">
              Backed by a 10,000+ sq. ft. facility and a 100+ strong team, we
              bring CNC &amp; VMC machining, aluminium die casting, fiber laser
              cutting and sheet metal fabrication together under one roof
              &mdash; with an unwavering focus on quality at every step, from
              prototyping through production, inspection and delivery.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/about" className="inline-flex items-center gap-2 bg-[#D80000] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#b00000]">
                Find Out More <ArrowRight className="w-4 h-4" />
              </Link>
              <button className="inline-flex items-center gap-2 border border-[#1a1a1a]/20 text-[#1a1a1a] font-semibold px-6 py-3 rounded-md hover:border-[#D80000] hover:text-[#D80000]">
                <Download className="w-4 h-4" /> Download Brochure
              </button>
            </div>
          </div>
          <div className="relative">
            <img src={aboutImage} alt="In-house manufacturing facility" className="rounded-lg shadow-xl w-full h-[380px] object-cover" />
            <div className="absolute -bottom-5 -left-5 hidden sm:block bg-[#1a1a1a] text-white rounded-lg px-6 py-4 shadow-lg">
              <p className="font-heading text-2xl font-bold">10,000+</p>
              <p className="text-xs text-white/70">sq. ft. manufacturing facility</p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions CTA */}
      <section className="bg-[#141414] py-16 md:py-20">
        <div className="max-w-[820px] mx-auto px-5 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">QUESTIONS?</h2>
          <p className="text-white/70 text-lg mt-4">
            Whether you&apos;re curious about our capabilities, need a quote, or
            want to discuss a project, we&apos;re here to answer any questions.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-8 bg-[#D80000] text-white font-semibold px-8 py-3.5 rounded-md hover:bg-[#b00000] hover:-translate-y-0.5">
            Let&apos;s Talk Now <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
